import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Briefcase, 
  CheckCircle, 
  ArrowRight,
} from "lucide-react";

import { HeroSection, StatsBar } from "../components/landing/HeroSection";
import { HowItWorks } from "../components/landing/HowItWorks";
import { SourcesSection } from "../components/landing/SourcesSection";
import { SampleOutput } from "../components/landing/SampleOutput";
import { TrustSection, Footer } from "../components/landing/TrustAndFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const formRef = useRef(null);
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    target_role: "",
    location: "",
    job_type: "remote",
    experience_level: "mid",
    linkedin_url: "",
    target_companies: "",
    notes: "",
    priority: "exploring",
  });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
    scrollToForm();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.target_role || !formData.location) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const plan = selectedPlan;
      const amount = plan === "starter" ? 1900 : 4900;
      const jobCount = plan === "starter" ? 20 : 50;
      
      const { data: submission } = await axios.post(`${API}/submit`, {
        ...formData,
        plan,
        job_count: jobCount,
        amount,
      });

      const razorpayLoaded = await loadRazorpay();
      if (!razorpayLoaded) {
        toast.error("Payment system failed to load. Please try again.");
        setLoading(false);
        return;
      }

      const { data: order } = await axios.post(
        `${API}/payment/create-order?submission_id=${submission.submission_id}&amount=${amount}`
      );

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "GetJobInterviews",
        description: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan - ${jobCount} Job Applications`,
        order_id: order.order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              submission_id: submission.submission_id,
            });
            window.location.href = `/confirmation/${submission.submission_id}`;
          } catch (err) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: formData.full_name,
          email: formData.email,
          contact: formData.phone,
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#002FA7]" />
            <span className="font-black text-xl tracking-tight text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              GetJobInterviews
            </span>
          </div>
          <Button 
            onClick={scrollToForm}
            data-testid="nav-cta-btn"
            className="bg-[#002FA7] text-white hover:bg-[#002280] rounded-sm"
          >
            Get Started
          </Button>
        </div>
      </nav>

      <HeroSection onScrollToForm={scrollToForm} />
      <StatsBar />
      <HowItWorks />
      <SourcesSection />
      <SampleOutput />

      {/* Pricing */}
      <section className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 
              className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Simple Pricing
            </h2>
            <p className="text-zinc-600 mt-2">Pay once, get results in 3 days</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <PlanCard
              name="Starter"
              price="19"
              jobs={20}
              features={["20 job applications", "Ready-to-apply links", "3-day delivery"]}
              isSelected={selectedPlan === "starter"}
              onSelect={() => selectPlan("starter")}
              testId="plan-starter"
            />
            <PlanCard
              name="Pro"
              price="49"
              jobs={50}
              features={["50 job applications", "Ready-to-apply links", "Cover letter snippets", "Priority 3-day delivery"]}
              isSelected={selectedPlan === "pro"}
              onSelect={() => selectPlan("pro")}
              testId="plan-pro"
              recommended
            />
          </div>
          
          <div className="bg-amber-50 border border-amber-200 p-4 text-center mt-6">
            <p className="text-amber-800 text-sm">
              <strong>Note:</strong> We focus on relevant job matches, not volume. 
              In some cases, you may receive fewer but higher-quality opportunities. 
              If you're not satisfied, we'll refine your batch once for free.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section ref={formRef} className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#002FA7]/10 text-[#002FA7] text-sm font-medium px-4 py-2 rounded-full mb-4">
              Step 1 of 2 — Tell us about yourself
            </div>
            <h2 
              className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Let's Find Your Next Job
            </h2>
            <p className="text-zinc-600 mt-2">
              Takes 2 minutes. We'll start working immediately after payment.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="text-lg font-semibold text-zinc-900 mb-4">Basic Info</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input data-testid="input-name" value={formData.full_name} onChange={(e) => handleInputChange("full_name", e.target.value)} placeholder="e.g. Sarah Johnson" required className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input data-testid="input-email" type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} placeholder="e.g. sarah@email.com" required className="rounded-sm" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Phone (optional)</Label>
                  <Input data-testid="input-phone" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} placeholder="e.g. +1 555 123 4567" className="rounded-sm" />
                </div>
              </div>
            </div>

            {/* Job Preferences */}
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="text-lg font-semibold text-zinc-900 mb-4">Job Preferences</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Target Role *</Label>
                  <Input data-testid="input-role" value={formData.target_role} onChange={(e) => handleInputChange("target_role", e.target.value)} placeholder="e.g. Product Manager, Software Engineer" required className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Input data-testid="input-location" value={formData.location} onChange={(e) => handleInputChange("location", e.target.value)} placeholder="e.g. Remote, New York, Bangalore" required className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Job Type</Label>
                  <Select value={formData.job_type} onValueChange={(value) => handleInputChange("job_type", value)}>
                    <SelectTrigger className="rounded-sm" data-testid="select-job-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="onsite">Onsite</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Experience Level</Label>
                  <Select value={formData.experience_level} onValueChange={(value) => handleInputChange("experience_level", value)}>
                    <SelectTrigger className="rounded-sm" data-testid="select-experience"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entry">Entry Level (0-2 years)</SelectItem>
                      <SelectItem value="mid">Mid Level (2-5 years)</SelectItem>
                      <SelectItem value="senior">Senior (5+ years)</SelectItem>
                      <SelectItem value="executive">Executive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Profile */}
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="text-lg font-semibold text-zinc-900 mb-4">Your Profile</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input data-testid="input-linkedin" value={formData.linkedin_url} onChange={(e) => handleInputChange("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/yourprofile" className="rounded-sm" />
                  <p className="text-xs text-zinc-500">We'll use your LinkedIn profile to understand your background</p>
                </div>
              </div>
            </div>

            {/* Optional Details */}
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="text-lg font-semibold text-zinc-900 mb-4">Optional Details</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Priority Level</Label>
                  <Select value={formData.priority} onValueChange={(value) => handleInputChange("priority", value)}>
                    <SelectTrigger className="rounded-sm" data-testid="select-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent (actively job hunting / unemployed)</SelectItem>
                      <SelectItem value="exploring">Exploring (casually looking)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">Helps us prioritize your request</p>
                </div>
                <div className="space-y-2">
                  <Label>Target Companies</Label>
                  <Input data-testid="input-companies" value={formData.target_companies} onChange={(e) => handleInputChange("target_companies", e.target.value)} placeholder="Google, Meta, startups, etc." className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea data-testid="input-notes" value={formData.notes} onChange={(e) => handleInputChange("notes", e.target.value)} placeholder="Any specific requirements or preferences..." className="rounded-sm min-h-[100px]" />
                </div>
              </div>
            </div>

            {/* Selected Plan Display */}
            <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-zinc-900">{selectedPlan === "starter" ? "Starter Plan" : "Pro Plan"}</p>
                  <p className="text-sm text-zinc-600">{selectedPlan === "starter" ? "20 job applications" : "50 job applications"}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-zinc-900">${selectedPlan === "starter" ? "19" : "49"}</p>
                  <p className="text-xs text-zinc-500">one-time payment</p>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="text-center mb-2">
              <span className="text-sm text-zinc-500">Step 2 of 2 — Complete payment</span>
            </div>
            <Button type="submit" data-testid="submit-btn" disabled={loading} className="w-full bg-[#002FA7] text-white hover:bg-[#002280] rounded-sm py-6 text-lg font-semibold">
              {loading ? "Processing..." : `Get My Job Applications — $${selectedPlan === "starter" ? "19" : "49"}`}
            </Button>
            <p className="text-center text-sm text-zinc-500">
              Secure payment via Razorpay. We start immediately after payment.
            </p>
          </form>
        </div>
      </section>

      <TrustSection />
      <Footer />
    </div>
  );
}

function PlanCard({ name, price, jobs, features, isSelected, onSelect, testId, recommended }) {
  return (
    <div
      className={`p-8 border-2 transition-all cursor-pointer relative ${
        isSelected ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 hover:border-zinc-300"
      }`}
      onClick={onSelect}
      data-testid={testId}
    >
      {recommended && (
        <div className="absolute -top-3 right-4 bg-[#002FA7] text-white text-xs font-bold px-3 py-1">
          RECOMMENDED
        </div>
      )}
      <h3 className="text-xl font-bold text-zinc-900 mb-2">{name}</h3>
      <div className="flex items-baseline gap-1 mb-4">
        <span className="text-4xl font-black text-zinc-900">${price}</span>
        <span className="text-zinc-500">one-time</span>
      </div>
      <ul className="space-y-3 mb-6">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-zinc-700">
            <CheckCircle className="w-5 h-5 text-green-600" />
            {f}
          </li>
        ))}
      </ul>
      <Button
        onClick={onSelect}
        variant={isSelected ? "default" : "outline"}
        className={`w-full rounded-sm ${isSelected ? "bg-[#002FA7] text-white" : ""}`}
      >
        {isSelected ? "Selected" : "Select Plan"}
      </Button>
    </div>
  );
}
